// ============================================================================
// MarketWalletSummary — верхняя карточка кошелька мастера (мобильный вид).
// Доступно — крупно, «на счёте» и «в резерве» — рядом. При нулевом доступном
// остатке показывается честное состояние «нет денег» с переходом к пополнению.
// Никаких таблиц и окон платформы — только карточки.
// ============================================================================

import { AlertTriangle, RefreshCw, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatValue } from '@/lib/format';
import type { MarketWalletData } from '@/lib/marketWallet';

interface MarketWalletSummaryProps {
  data: MarketWalletData;
  refreshing: boolean;
  onRefresh: () => void;
  onTopUp: () => void;
}

function money(value: number): string {
  return formatValue(value, 'number:0;group', 'numeric');
}

export function MarketWalletSummary({
  data,
  refreshing,
  onRefresh,
  onTopUp,
}: MarketWalletSummaryProps) {
  const { balance } = data;
  const noMoney = balance.available <= 0;
  const loaded = data.loadedAt
    ? formatValue(data.loadedAt, 'datetime:dd.MM.yyyy HH:mm', 'timestamp')
    : '';

  return (
    <section className="space-y-3">
      <div className="rounded-3xl border border-primary/20 bg-gradient-to-br from-primary/15 via-primary/5 to-background p-4 shadow-sm sm:p-5">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15 text-primary">
            <Wallet className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold">{data.accountName}</div>
            <div className="text-[11px] text-muted-foreground">
              {loaded ? `Актуально на ${loaded}` : 'Личный счёт мастера'}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-9 w-9 shrink-0 rounded-full p-0"
            onClick={onRefresh}
            disabled={refreshing}
            aria-label="Обновить кошелёк"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Доступно
          </div>
          <div className="mt-0.5 flex flex-wrap items-baseline gap-2">
            <span className="text-3xl font-semibold tabular-nums leading-none">
              {money(balance.available)}
            </span>
            {balance.currency && (
              <span className="text-sm font-medium text-muted-foreground">{balance.currency}</span>
            )}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <div className="rounded-2xl border bg-background/60 px-3 py-2.5">
            <div className="text-[11px] text-muted-foreground">На счёте</div>
            <div className="mt-0.5 text-base font-semibold tabular-nums">{money(balance.balance)}</div>
          </div>
          <div className="rounded-2xl border bg-background/60 px-3 py-2.5">
            <div className="text-[11px] text-muted-foreground">В резерве</div>
            <div className="mt-0.5 text-base font-semibold tabular-nums">{money(balance.holds)}</div>
          </div>
        </div>
      </div>

      {noMoney && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-3.5">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <div className="min-w-0 flex-1 text-xs leading-5 text-amber-800">
              <div className="font-medium">
                {balance.available < 0 ? 'Отрицательный доступный остаток' : 'Недостаточно средств'}
              </div>
              <div className="mt-0.5 break-words">
                Взятие заявок списывает стоимость с личного счёта. Пополните кошелёк,
                чтобы брать новые заявки.
              </div>
              <Button
                size="sm"
                className="mt-2 h-8 rounded-lg"
                onClick={onTopUp}
              >
                Пополнить счёт
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
